import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useState } from 'react'
import { ButtonGroup, Modal } from 'stories/components'
import { getPrice2decimal, InputConvert, InputValidate, Num, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { ILoanCharge } from './types'

/** The two ways to waive: the whole of what is left, or a figure the servicer types. */
const FULL = 'Full amount'
const PARTIAL = 'Partial amount'

// A charge's interest-from date is stored as YYYY-MM-DD; guard the test like the server does.
const YMD = /^\d{4}-\d{1,2}-\d{1,2}$/

const round2 = (value: number) => Number(value.toFixed(2))

const defaultInputs = (): Record<string, InputType> => ({
  amount: {
    inputType: 'text',
    type: 'thousandSep',
    title: 'Amount to Waive',
    prefix: '$',
    value: '',
    required: true,
  },
  reason: {
    inputType: 'textarea',
    type: 'text',
    title: 'Reason (optional)',
    placeholder: 'Why is it being waived?',
    value: '',
    rows: 3,
    span: 'full',
    required: false,
  },
})

/**
 * Waiving a charge: forgive what is owed on it instead of collecting it, in full or in part.
 *
 * A servicer letting a whole fee go is the common case and stays one click, so the amount field
 * only appears once they say they are waiving part of it. The partial figure comes off the charge's
 * unpaid balance exactly as a payment of the same size would, and the remainder keeps billing -
 * which is the whole point of a partial waiver, so the modal says what will be left rather than
 * making them work it out.
 */
export const WaiveLoanCharge = ({
  item,
  onClose,
  onSubmit,
}: {
  item: ILoanCharge
  onClose: () => void
  /** `amount` is omitted on a full waiver, which is how the server is told to take everything. */
  onSubmit: (data: { amount?: number; reason: string }) => void
}) => {
  const [mode, setMode] = useState(FULL)
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [loading, setLoading] = useState(false)

  const balance = Num(item.unpaidBalance)
  const accrued = Num(item.accruedInterest)
  const due = round2(balance + accrued)
  const isPartial = mode === PARTIAL
  const amount = Num(removeComma(inputs.amount.value))
  // The server treats anything that would clear the balance as a full waiver, since what is left
  // could not be billed on its own. Say so rather than letting the figures surprise them.
  const clearsCharge = isPartial && amount > 0 && amount >= balance
  /**
   * Whether the interest on this charge is derived from its balance rather than a frozen figure.
   * It matters to what a partial waiver leaves behind: the amount comes off the balance, and on
   * such a charge the interest falls with it, so what is left owing drops by MORE than was waived.
   * Interest is linear in the balance, so the remainder is the due scaled by the balance that
   * survives - exact without having to know the loan's daily-rate basis, which only the server has.
   */
  const accruesOnBalance = Num(item.interestRate) > 0 && YMD.test(String(item.interestFromDate || '').substring(0, 10))
  const scalesWithBalance = accruesOnBalance && accrued > 0 && balance > 0
  const remaining = clearsCharge ? 0 : scalesWithBalance ? round2((due * (balance - amount)) / balance) : due - amount

  const money = (value: number) => `$${getPrice2decimal(value, false, true)}`

  const onChange = (key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onOk = () => {
    if (!isPartial) {
      setLoading(true)
      onSubmit({ reason: String(inputs.reason.value || '') })
      return
    }
    const newInputs = cloneDeep(inputs)
    newInputs.amount.error = InputValidate(newInputs.amount)
    if (!newInputs.amount.error) {
      if (!(amount > 0)) newInputs.amount.error = 'Enter how much of the charge is being waived.'
      else if (amount > due) newInputs.amount.error = `There is only ${money(due)} left on this charge.`
    }
    setInputs(newInputs)
    if (newInputs.amount.error) return
    setLoading(true)
    onSubmit({ amount, reason: String(inputs.reason.value || '') })
  }

  return (
    <Modal
      title="Waive Charge"
      titleOkay="Waive Charge"
      loading={loading}
      onClose={onClose}
      onOk={onOk}
      childLevel={2}
      isOpen
    >
      <div className="w-120 text-gray-900">
        <p className="mb-1 text-[15px]">
          <span className="font-semibold">{item.description || 'This charge'}</span> owes{' '}
          <span className="font-semibold">{money(due)}</span>
          {accrued > 0 && (
            <span className="text-gray-500">
              {' '}
              ({money(balance)} balance + {money(accrued)} interest)
            </span>
          )}
          .
        </p>
        <p className="mb-4 text-[13px] text-gray-500">
          The charge stays on the loan as a record of the fee, with what was waived shown against it.
        </p>

        <ButtonGroup title={[FULL, PARTIAL]} value={mode} onChange={setMode} />

        <div className="mt-4 grid gap-4 grid-cols-1">
          {isPartial && (
            <div className="input">
              <RenderInput input={inputs.amount} Key="amount" onChange={onChange} />
            </div>
          )}
          <div className="input">
            <RenderInput input={inputs.reason} Key="reason" onChange={onChange} />
          </div>
        </div>

        <p className="mt-3 text-[13px] text-gray-600">
          {!isPartial || clearsCharge ? (
            <>
              Waiving the whole {money(due)}. Nothing further is billed, collected or quoted on this
              charge.
            </>
          ) : amount > 0 && amount <= due ? (
            <>
              Waiving {money(amount)} leaves <span className="font-semibold">{money(remaining)}</span>{' '}
              owing, which keeps billing as normal.
              {scalesWithBalance && (
                <>
                  {' '}
                  The amount comes off the balance, and the interest standing on this charge falls
                  with it.
                </>
              )}
            </>
          ) : (
            <>Up to {money(due)} can be waived. Whatever is left keeps billing as normal.</>
          )}
        </p>
      </div>
    </Modal>
  )
}
